// src/components/PatronManagementView/QuickCreateCredentialModal.js
import React, { useState, useEffect, useCallback } from "react";
import config from "../../config";
import logger from "../../utils/logger";
import "./AddPhysicalCredentialModal.css";
import "./QuickCreateCredentialModal.css";

const FORMAT_LIMITS = {
  "26A": { facilityMax: 255, badgeMax: 65535 },
  "32X": { facilityMax: null, badgeMax: 1073741823 },
  "35X": { facilityMax: 4095, badgeMax: 1048575 },
  "36X": { facilityMax: 4095, badgeMax: 2097151 },
  "37X": { facilityMax: 65535, badgeMax: 524287 },
  "40X": { facilityMax: 1023, badgeMax: 268435455 },
  "48X": { facilityMax: 1048575, badgeMax: 1048575 },
  "56W": { facilityMax: 16777215, badgeMax: 4294967295 },
};

const BIT_FORMATS = Object.keys(FORMAT_LIMITS);

const TRANSPORT_TECHNOLOGIES = [
  { value: "ANY",               label: "Any Type",                    description: "Allows credential to be accepted by device no matter what technology it uses." },
  { value: "PROX",              label: "Prox Card",                   description: "Allows credential to be accepted by device only if it uses 125KHz Proximity technology." },
  { value: "UID",               label: "Smart Credential (UID)",      description: "Allows credential to be accepted by device only when credential is a smart credential's unencrypted UID or serial number." },
  { value: "MIFARE_CLASSIC",    label: "MiFare Classic",              description: "Allows credential to be accepted by device only if it uses Mifare Classic technology." },
  { value: "MIFARE_DESFIRE",    label: "MiFare DesFire",              description: "Allows credential to be accepted by device only if it uses Mifare Desfire technology." },
  { value: "APPLE_NFC",         label: "Apple NFC Phone/Watch (use Create Patron)",       description: "Allows credential to be accepted by device only if it uses Apple NFC technology.", disabled: true },
  { value: "ANDROID_MIFARE2GO", label: "Android NFC Phone/Wearable (use Create Patron)", description: "Allows credential to be accepted by device only if it uses Android Mifare-2-Go technology.", disabled: true },
  { value: "ALLEGION_BLE",      label: "Allegion BLE",                description: "Allows credential to be accepted by device only if it uses Allegion Bluetooth technology." },
];

const sanitizeName = (name) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "");

const buildEmail = (firstName, lastName, suffix) => {
  const first = sanitizeName(firstName) || "user";
  const last = sanitizeName(lastName) || "patron";
  const suffixStr = suffix > 1 ? String(suffix) : "";
  return `${first}.${last}${suffixStr}@romanworks.com`;
};

const QuickCreateCredentialModal = ({ organizationId, getAccessToken, onSuccess, onClose }) => {
  const orgId = organizationId;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [transportTechnology, setTransportTechnology] = useState("ANY");
  const [bitFormat, setBitFormat] = useState("");
  const [facilityCode, setFacilityCode] = useState(55);
  const [badgeId, setBadgeId] = useState("");
  const [issueCode, setIssueCode] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const limits = bitFormat ? FORMAT_LIMITS[bitFormat] : null;

  // Reset facilityCode to default when bitFormat changes
  useEffect(() => {
    setFacilityCode(55);
  }, [bitFormat]);

  // ESC key: block during submission; call onSuccess during success state
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key !== "Escape") return;
      if (submitting) return;
      if (successMessage) {
        onSuccess();
      } else {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [submitting, successMessage, onSuccess, onClose]);

  const handleDismiss = useCallback(() => {
    if (submitting) return;
    if (successMessage) {
      onSuccess();
    } else {
      onClose();
    }
  }, [submitting, successMessage, onSuccess, onClose]);

  const rejectNonDigits = (e) => {
    if (
      !/[\d]/.test(e.key) &&
      !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)
    ) {
      e.preventDefault();
    }
  };

  const validate = () => {
    if (!firstName.trim()) return "First name is required.";
    if (!lastName.trim()) return "Last name is required.";
    if (!bitFormat) return "Please select a bit format.";
    if (!limits) return "Unknown bit format.";

    const badgeIdNum = Number(badgeId);
    if (badgeId === "" || !Number.isInteger(badgeIdNum) || badgeIdNum < 0 || badgeIdNum > limits.badgeMax) {
      return `Badge ID must be an integer between 0 and ${limits.badgeMax.toLocaleString()}.`;
    }

    if (bitFormat !== "32X") {
      const fcNum = Number(facilityCode);
      if (!Number.isInteger(fcNum) || fcNum < 0 || fcNum > limits.facilityMax) {
        return `Facility Code must be an integer between 0 and ${limits.facilityMax.toLocaleString()}.`;
      }
    }

    if (bitFormat === "48X") {
      const icNum = Number(issueCode);
      if (!Number.isInteger(icNum) || icNum < 1 || icNum > 63) {
        return "Issue Code must be an integer between 1 and 63.";
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    let patronId = null;

    try {
      // Fetch default badge photo
      const photoResponse = await fetch("/images/default-pass-photo.png");
      if (!photoResponse.ok) throw new Error("photo_fetch_failed");
      const photoBlob = await photoResponse.blob();

      // Obtain access token once before retry loop
      const accessToken = await getAccessToken();

      // Build patron via retry loop (handles cross-org 409)
      let suffix = 1;
      let patronCreated = false;
      let patronData = null;

      while (suffix <= 10 && !patronCreated) {
        const email = buildEmail(firstName, lastName, suffix);

        const formData = new FormData();
        formData.append("orgId", orgId);
        formData.append("firstName", firstName.trim());
        formData.append("lastName", lastName.trim());
        formData.append("email", email);
        formData.append("mobileNumber", "+10000000000");
        formData.append("isQuickCreated", "true");
        formData.append("badgePhoto", photoBlob, "default-pass-photo.png");

        const patronResponse = await fetch(`${config.apiEndpoint}/patrons`, {
          method: "POST",
          headers: {
            "x-api-key": config.apiKey,
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        });

        if (patronResponse.status === 409) {
          suffix++;
          continue;
        }

        const patronResult = await patronResponse.json().catch(() => ({}));

        if (!patronResponse.ok) {
          throw new Error(patronResult.message || `Error ${patronResponse.status}: Failed to create patron.`);
        }

        if (patronResult.warning) {
          logger.warn("[QuickCreate] Auth0 warning:", patronResult.warning);
        }

        patronData = patronResult.patron;
        patronId = patronData?.patronId;
        patronCreated = true;
      }

      if (!patronCreated) {
        throw new Error(
          "Unable to create patron: a conflicting account already exists. Please try a different name or contact support."
        );
      }

      // Create physical credential
      const credBody = {
        bitFormat,
        badgeId: Number(badgeId),
        patronId,
        orgId,
        transportTechnology,
      };
      if (bitFormat !== "32X") credBody.facilityCode = Number(facilityCode);
      if (bitFormat === "48X") credBody.issueCode = Number(issueCode);

      const credResponse = await fetch(`${config.servicesApiEndpoint}/physical-credentials`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": config.apiKey,
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(credBody),
      });

      const credData = await credResponse.json().catch(() => ({}));

      if (!credResponse.ok) {
        // Rollback: delete the patron (full cascade in delete-patron.mjs)
        await fetch(`${config.apiEndpoint}/patrons/${orgId}/${patronId}`, {
          method: "DELETE",
          headers: {
            "x-api-key": config.apiKey,
            Authorization: `Bearer ${accessToken}`,
          },
        }).catch(() => {});
        patronId = null;

        throw new Error(credData.error || `Error ${credResponse.status}: Failed to create credential.`);
      }

      const deviceIds = credData.deviceIds ? [...credData.deviceIds] : [];
      const credMsg =
        deviceIds.length === 0
          ? "Physical credential created. No devices found in this organization — the credential will be provisioned automatically when devices are connected."
          : `Physical credential created and queued for provisioning to ${deviceIds.length} device${deviceIds.length !== 1 ? "s" : ""}.`;

      setSuccessMessage(
        `✓ Patron ${firstName.trim()} ${lastName.trim()} created successfully.\n${credMsg}`
      );
    } catch (err) {
      if (err.message === "photo_fetch_failed") {
        setError("Failed to load default badge photo. Please contact support.");
      } else {
        setError(err.message || "An unexpected error occurred.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (successMessage) {
    return (
      <div className="quick-create-modal-overlay" onClick={onSuccess}>
        <div className="quick-create-modal" onClick={(e) => e.stopPropagation()}>
          <div className="add-physical-credential-header">
            <h2>⚡ Patron &amp; Credential Created</h2>
            <button className="add-physical-credential-close-btn" onClick={onSuccess}>
              ✕
            </button>
          </div>
          <div className="quick-create-success-body">
            {successMessage.split("\n").map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
          <div className="add-physical-credential-actions">
            <button className="add-physical-credential-submit-btn" onClick={onSuccess}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quick-create-modal-overlay" onClick={handleDismiss}>
      <div className="quick-create-modal" onClick={(e) => e.stopPropagation()}>
        <div className="add-physical-credential-header">
          <h2>⚡➕ Quick-Create Credential</h2>
          <button
            className="add-physical-credential-close-btn"
            onClick={handleDismiss}
            disabled={submitting}
          >
            ✕
          </button>
        </div>

        <div className="quick-create-warning">
          ⚠️ To create a Romanworks NFC mobile credential, use the <strong>Create Patron</strong> feature instead. Patrons created via Quick-Create do not receive an access right to download a mobile credential to their wallet.
        </div>

        <form className="add-physical-credential-form" onSubmit={handleSubmit}>
          <div className="add-physical-credential-field">
            <label htmlFor="qc-firstName">First Name</label>
            <input
              id="qc-firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={submitting}
              required
            />
          </div>

          <div className="add-physical-credential-field">
            <label htmlFor="qc-lastName">Last Name</label>
            <input
              id="qc-lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={submitting}
              required
            />
          </div>

          <div className="add-physical-credential-field">
            <label htmlFor="qc-transportTechnology">Technology</label>
            <select
              id="qc-transportTechnology"
              value={transportTechnology}
              onChange={(e) => setTransportTechnology(e.target.value)}
              disabled={submitting}
            >
              {TRANSPORT_TECHNOLOGIES.map((tech) => (
                <option key={tech.value} value={tech.value} disabled={tech.disabled}>{tech.label}</option>
              ))}
            </select>
            <p className="add-physical-credential-helper-text">
              {TRANSPORT_TECHNOLOGIES.find((t) => t.value === transportTechnology)?.description}
            </p>
          </div>

          <div className="add-physical-credential-field">
            <label htmlFor="qc-bitFormat">Bit Format</label>
            <select
              id="qc-bitFormat"
              value={bitFormat}
              onChange={(e) => { setBitFormat(e.target.value); setError(null); }}
              disabled={submitting}
              required
            >
              <option value="">— Select format —</option>
              {BIT_FORMATS.map((fmt) => (
                <option key={fmt} value={fmt}>{fmt}</option>
              ))}
            </select>
          </div>

          {bitFormat === "48X" && (
            <div className="add-physical-credential-field">
              <label htmlFor="qc-issueCode">Issue Code (1–63)</label>
              <input
                id="qc-issueCode"
                type="number"
                min={1}
                max={63}
                value={issueCode}
                onChange={(e) => setIssueCode(e.target.value)}
                onKeyDown={rejectNonDigits}
                disabled={submitting}
              />
            </div>
          )}

          {bitFormat && bitFormat !== "32X" && (
            <div className="add-physical-credential-field">
              <label htmlFor="qc-facilityCode">
                Facility Code {limits ? `(0–${limits.facilityMax.toLocaleString()})` : ""}
              </label>
              <input
                id="qc-facilityCode"
                type="number"
                min={0}
                max={limits?.facilityMax}
                value={facilityCode}
                onChange={(e) => setFacilityCode(e.target.value)}
                onKeyDown={rejectNonDigits}
                disabled={submitting}
              />
            </div>
          )}

          {bitFormat && (
            <div className="add-physical-credential-field">
              <label htmlFor="qc-badgeId">
                Badge ID {limits ? `(0–${limits.badgeMax.toLocaleString()})` : ""}
              </label>
              <input
                id="qc-badgeId"
                type="text"
                inputMode="numeric"
                value={badgeId}
                onChange={(e) => setBadgeId(e.target.value)}
                onKeyDown={rejectNonDigits}
                disabled={submitting}
                required
              />
            </div>
          )}

          {error && <div className="add-physical-credential-error">{error}</div>}

          <div className="add-physical-credential-actions">
            <button
              type="button"
              className="add-physical-credential-cancel-btn"
              onClick={handleDismiss}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="add-physical-credential-submit-btn quick-create-submit-btn"
              disabled={submitting || !bitFormat || badgeId === "" || !firstName.trim() || !lastName.trim()}
            >
              {submitting ? "Creating…" : "⚡ Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickCreateCredentialModal;
